import { useCallback, useMemo } from 'react';
import { noop } from '../../../../../utils/noop';

/**
 * Custom hook that encapsulates all business logic for ImageGalleryControlButtons
 * @param {Object} props - Component props
 * @param {Function} props.onPreviousHeadLine - Handler for previous button click
 * @param {Function} props.onNextHeadline - Handler for next button click
 * @param {Function} props.onMoveOver - Handler for mouse move events
 * @param {string} props.variant - Button variant style
 * @param {boolean} props.isConnect - Whether this is a connect button variant
 * @returns {Object} - Processed data and handlers for control buttons
 */
export const useImageGalleryControlButtons = ({
  onPreviousHeadLine,
  onNextHeadline,
  onMoveOver = noop,
  variant = 'transparent',
  isConnect = false,
}) => {
  // Generate CSS classes for buttons based on variant and connect state
  const getButtonClasses = useCallback(
    (direction) => {
      const connectClass = isConnect ? 'connect-button' : '';
      return `headline-control ${direction} ${variant} ${connectClass}`.trim();
    },
    [variant, isConnect]
  );

  // Memoized button configurations
  const leftButtonConfig = useMemo(
    () => ({
      id: 'PDP_swipe_photo_left',
      className: getButtonClasses('left'),
      onClick: onPreviousHeadLine,
      onMouseMove: onMoveOver,
      iconClass: 'icon csicon-arrow_left',
      'aria-label': 'Previous image',
    }),
    [getButtonClasses, onPreviousHeadLine, onMoveOver]
  );

  const rightButtonConfig = useMemo(
    () => ({
      id: 'PDP_swipe_photo_right',
      className: getButtonClasses('right'),
      onClick: onNextHeadline,
      onMouseMove: onMoveOver,
      iconClass: 'icon i-gallery csicon-arrow_right',
      'aria-label': 'Next image',
    }),
    [getButtonClasses, onNextHeadline, onMoveOver]
  );

  // Validation logic
  const shouldRender = useMemo(() => {
    return typeof onPreviousHeadLine === 'function' && typeof onNextHeadline === 'function';
  }, [onPreviousHeadLine, onNextHeadline]);

  // Button variant validation
  const isValidVariant = useMemo(() => {
    const validVariants = ['opaque', 'transparent', 'smallAndTransparent'];
    return validVariants.includes(variant);
  }, [variant]);

  return {
    // Button configurations
    leftButtonConfig,
    rightButtonConfig,

    // State values
    shouldRender,
    isValidVariant,
    isConnect,
    variant,

    // Computed values
    connectClass: isConnect ? 'connect-button' : '',

    // Utility functions
    getButtonClasses,
  };
};
